import { useEffect, useRef, useState } from "react";
import { apiRequest, isAbortError } from "../../lib/apiClient";
import { useCollection } from "../../lib/useCollection";
import { useToast } from "../../components/ui/ToastProvider";
import { AsyncSection } from "../../components/ui/AsyncSection";
import { TextField, TextAreaField, SelectField, FileField } from "../../components/ui/Field";
import { NEWS_CATEGORIES, toDateInput } from "../../config/constants";

const EMPTY_FORM = { title: "", excerpt: "", category: "", date: "", link: "" };

/* The single story pinned to the top of the news page.
 *
 * The update handler for this existed but was never wired to any markup, so the
 * featured story could only ever be changed straight in the database. */
export function FeaturedStoryCard() {
  const { status, data: featured, error, reload } = useCollection(
    "/api/featured-news",
    (payload) => payload || {}
  );

  const [form, setForm] = useState(EMPTY_FORM);
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();

  // Seed the form once the current story arrives.
  useEffect(() => {
    if (!featured) return;
    setForm({
      title: featured.title || "",
      excerpt: featured.excerpt || "",
      category: featured.category || "",
      date: toDateInput(featured.date),
      link: featured.link || "",
    });
  }, [featured]);

  const updateField = (field) => (value) => setForm((current) => ({ ...current, [field]: value }));

  const handleSave = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      toast.error("A title is required.");
      return;
    }
    if (!form.category) {
      toast.error("Choose a category.");
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => formData.append(key, value));
    if (image) formData.append("image", image);

    setSaving(true);
    try {
      await apiRequest("/api/featured-news/update", { method: "PUT", auth: true, body: formData });

      setImage(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Featured story updated.");
      reload();
    } catch (err) {
      if (!isAbortError(err)) toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="upload-card">
      <h2 id="featured-heading">Featured story</h2>
      <p className="card-description">
        Pinned to the top of the news page. There is only ever one.
      </p>

      <AsyncSection status={status} error={error} isEmpty={false} onRetry={reload}>
        <form onSubmit={handleSave} className="news-form-stack">
          <TextField
            label="Title"
            value={form.title}
            onChange={updateField("title")}
            disabled={saving}
            required
          />

          <TextAreaField
            label="Excerpt"
            value={form.excerpt}
            onChange={updateField("excerpt")}
            disabled={saving}
          />

          <div className="form-row-split">
            <SelectField
              label="Category"
              value={form.category}
              onChange={updateField("category")}
              options={NEWS_CATEGORIES}
              placeholder="Select a category…"
              disabled={saving}
              required
            />

            <TextField
              label="Publish date"
              type="date"
              value={form.date}
              onChange={updateField("date")}
              disabled={saving}
            />
          </div>

          <TextField
            label="Full article URL"
            type="url"
            value={form.link}
            onChange={updateField("link")}
            disabled={saving}
          />

          <div className="form-row-split">
            <FileField
              label="Cover image"
              hint={featured?.image ? "Uploading replaces the current image." : "Maximum 10MB."}
              inputRef={fileInputRef}
              onChange={setImage}
              disabled={saving}
            />

            <button type="submit" disabled={saving}>
              {saving ? "Saving…" : "Update featured story"}
            </button>
          </div>
        </form>
      </AsyncSection>
    </div>
  );
}
